import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { BACKEND_URL } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MapPin, Phone, Globe, Instagram, Facebook, Youtube, Linkedin, Mail, Camera, Video, Store, Tag, CalendarDays, Clock } from "lucide-react";
import { MapContainer, TileLayer, Marker } from "react-leaflet";

const DAYS = [
  { k: "mon", label: "Dilluns" }, { k: "tue", label: "Dimarts" }, { k: "wed", label: "Dimecres" },
  { k: "thu", label: "Dijous" }, { k: "fri", label: "Divendres" }, { k: "sat", label: "Dissabte" },
  { k: "sun", label: "Diumenge" },
];

function resolveUrl(u) {
  if (!u) return "";
  if (u.startsWith("/")) return `${BACKEND_URL}${u}`;
  return u;
}

const SOCIAL_ICONS = { instagram: Instagram, facebook: Facebook, youtube: Youtube, linkedin: Linkedin };

export default function PublicProfile() {
  const { memberNumber } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    api.get(`/public/associates/${memberNumber}`)
      .then(({ data }) => setData(data))
      .catch(() => setError("Comerç no trobat"));
  }, [memberNumber]);

  if (error) return <PublicLayout><div className="max-w-lg mx-auto py-24 text-center"><div className="font-heading font-bold text-2xl">Comerç no trobat</div><Button asChild className="mt-6 rounded-full"><Link to="/directori">Tornar al directori</Link></Button></div></PublicLayout>;
  if (!data) return <PublicLayout><div className="text-center py-24 text-muted-foreground">Carregant…</div></PublicLayout>;

  const a = data.associate;
  const gallery = a.gallery || [];
  const social = a.social || {};
  const socialEntries = Object.entries(social).filter(([, v]) => v && v.trim());
  const promotions = data.promotions || [];
  const hasHours = a.opening_hours && Object.keys(a.opening_hours).length > 0;

  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Header hero */}
        <div className="grid lg:grid-cols-5 gap-8" data-testid="public-profile">
          <div className="lg:col-span-3">
            {gallery.length > 0 ? (
              <div className="rounded-3xl overflow-hidden border-2 border-border" data-testid="profile-gallery">
                <div className="aspect-video">
                  <img src={resolveUrl(gallery[activeImg])} alt="" className="w-full h-full object-cover" />
                </div>
                {gallery.length > 1 && (
                  <div className="flex gap-2 p-3 overflow-x-auto">
                    {gallery.map((g, i) => (
                      <button key={i} onClick={() => setActiveImg(i)} className={`shrink-0 aspect-video w-24 rounded-lg overflow-hidden border-2 ${activeImg === i ? "border-accent" : "border-border"}`} data-testid={`profile-gallery-thumb-${i}`}>
                        <img src={resolveUrl(g)} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="aspect-video rounded-3xl bg-primary/10 flex items-center justify-center border-2 border-border">
                <Store className="w-16 h-16 text-primary/40" />
              </div>
            )}
          </div>

          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-start gap-4">
              {a.logo_url ? (
                <img src={resolveUrl(a.logo_url)} alt="" className="w-20 h-20 rounded-2xl object-cover border-2 border-border shrink-0" />
              ) : (
                <div className="w-20 h-20 rounded-2xl bg-secondary flex items-center justify-center font-heading font-bold text-2xl text-primary">{a.commercial_name.slice(0, 2).toUpperCase()}</div>
              )}
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-accent font-semibold">{a.category}</div>
                <h1 className="font-heading font-bold text-2xl leading-tight mt-1">{a.commercial_name}</h1>
                <div className="text-xs text-muted-foreground mt-1 font-mono">{a.member_number}</div>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              {a.address && <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" /><span>{a.address}</span></div>}
              {a.phone && <a href={`tel:${a.phone}`} className="flex items-center gap-2 hover:text-primary"><Phone className="w-4 h-4 text-muted-foreground" />{a.phone}</a>}
              {a.email && <a href={`mailto:${a.email}`} className="flex items-center gap-2 hover:text-primary"><Mail className="w-4 h-4 text-muted-foreground" />{a.email}</a>}
              {a.website && <a href={a.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-primary"><Globe className="w-4 h-4 text-muted-foreground" />Web</a>}
              {a.virtual_tour_url && <a href={a.virtual_tour_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-primary"><Video className="w-4 h-4 text-muted-foreground" />Tur virtual 360°</a>}
            </div>

            {socialEntries.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2" data-testid="profile-social">
                {socialEntries.map(([k, v]) => {
                  const Icon = SOCIAL_ICONS[k];
                  return (
                    <a key={k} href={v} target="_blank" rel="noreferrer" title={k} className="w-10 h-10 rounded-xl bg-primary text-primary-foreground hover:opacity-80 transition-opacity flex items-center justify-center capitalize text-xs font-bold" data-testid={`profile-social-${k}`}>
                      {Icon ? <Icon className="w-4 h-4" /> : k.slice(0, 2)}
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue={hasHours ? "hours" : (promotions.length > 0 ? "promos" : "info")} className="mt-12">
          <TabsList className="rounded-full">
            <TabsTrigger value="hours" className="rounded-full" data-testid="profile-tab-hours"><Clock className="w-3.5 h-3.5 mr-1" /> Horaris</TabsTrigger>
            <TabsTrigger value="promos" className="rounded-full" data-testid="profile-tab-promos"><Tag className="w-3.5 h-3.5 mr-1" /> Promocions ({promotions.length})</TabsTrigger>
            <TabsTrigger value="location" className="rounded-full" data-testid="profile-tab-location"><MapPin className="w-3.5 h-3.5 mr-1" /> Ubicació</TabsTrigger>
          </TabsList>

          <TabsContent value="hours" className="mt-6">
            {hasHours ? (
              <Card className="rounded-2xl border-2"><CardContent className="p-6">
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                  {DAYS.map(({ k, label }) => {
                    const slots = a.opening_hours?.[k] || [];
                    return (
                      <div key={k} className="flex items-center justify-between py-2 border-b border-border/60 last:border-0">
                        <span className="font-medium">{label}</span>
                        <span className="text-muted-foreground">
                          {slots.length === 0 ? "Tancat" : slots.map((s) => `${s.open}–${s.close}`).join(" · ")}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {(a.holidays || []).length > 0 && (
                  <div className="mt-6 pt-6 border-t">
                    <div className="text-xs uppercase tracking-widest text-accent font-semibold mb-3 flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5" /> Dies especials</div>
                    <ul className="text-sm space-y-1">
                      {a.holidays.map((h, i) => (
                        <li key={i} className="flex justify-between">
                          <span><strong>{h.date}</strong> {h.label && `· ${h.label}`}</span>
                          <span className="text-muted-foreground">{h.closed ? "Tancat" : "Horari especial"}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent></Card>
            ) : (
              <div className="text-center py-10 text-muted-foreground text-sm">Aquest comerç encara no ha publicat els seus horaris.</div>
            )}
          </TabsContent>

          <TabsContent value="promos" className="mt-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {promotions.map(p => (
                <Card key={p.id} className="rounded-2xl border-2 overflow-hidden" data-testid={`profile-promo-${p.id}`}>
                  {p.image_url ? <img src={p.image_url} alt="" className="aspect-video w-full object-cover" /> : <div className="aspect-video bg-primary/10 flex items-center justify-center"><Tag className="w-8 h-8 text-primary/40" /></div>}
                  <CardContent className="p-5">
                    <div className="font-heading font-semibold">{p.title}</div>
                    <div className="text-sm text-muted-foreground line-clamp-2 mt-1">{p.description}</div>
                    <div className="text-xs text-muted-foreground mt-2">Fins {p.end_date?.slice(0, 10)}</div>
                  </CardContent>
                </Card>
              ))}
              {promotions.length === 0 && <div className="col-span-full text-center py-10 text-muted-foreground text-sm">Sense promocions actives.</div>}
            </div>
          </TabsContent>

          <TabsContent value="location" className="mt-6">
            {a.lat && a.lng ? (
              <Card className="rounded-2xl border-2 overflow-hidden">
                <div style={{ height: 400 }} data-testid="profile-map">
                  <MapContainer center={[a.lat, a.lng]} zoom={16} style={{ height: "100%", width: "100%" }}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OSM" />
                    <Marker position={[a.lat, a.lng]} />
                  </MapContainer>
                </div>
              </Card>
            ) : (
              <div className="text-center py-10 text-muted-foreground text-sm">Encara no s'han publicat coordenades.</div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </PublicLayout>
  );
}
